// Hook React pour gérer les notifications push navigateur
import { useState, useEffect, useCallback } from 'react';

import { API_URL } from '../config/api';

// Convertir la clé VAPID base64url en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [swRegistration, setSwRegistration] = useState(null);

  const token = localStorage.getItem('ecosystem_token');

  // Vérifier le support et l'état initial
  useEffect(() => {
    const checkSupport = async () => {
      // Vérifier le support des notifications et service workers
      const supported = 'serviceWorker' in navigator && 
                        'PushManager' in window && 
                        'Notification' in window;
      
      setIsSupported(supported);
      
      if (!supported) {
        console.log('[PUSH] Push notifications not supported');
        return;
      }

      // Vérifier la permission actuelle
      setPermission(Notification.permission);

      // Enregistrer le Service Worker
      try {
        const registration = await navigator.serviceWorker.register('/sw-push.js');
        console.log('[PUSH] Service Worker registered');
        setSwRegistration(registration);

        // Vérifier si déjà abonné
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
        
        if (subscription) {
          console.log('[PUSH] Already subscribed');
        }
      } catch (err) {
        console.error('[PUSH] Service Worker registration failed:', err);
        setError('Impossible d\'enregistrer le Service Worker');
      }
    };

    checkSupport();
  }, []);

  // Demander la permission et s'abonner
  const subscribe = useCallback(async () => {
    if (!isSupported || !swRegistration || !token) {
      setError('Notifications non supportées ou non connecté');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Demander la permission si nécessaire
      if (Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        setPermission(perm);
        
        if (perm !== 'granted') {
          setError('Permission refusée');
          setLoading(false);
          return false;
        }
      } else if (Notification.permission === 'denied') {
        setError('Notifications bloquées. Modifiez les paramètres du navigateur.');
        setLoading(false);
        return false;
      }

      // Récupérer la clé VAPID du serveur
      const vapidResponse = await fetch(`${API_URL}/api/push/vapid-public-key`);
      if (!vapidResponse.ok) {
        throw new Error('Impossible de récupérer la clé VAPID');
      }
      const { publicKey } = await vapidResponse.json();

      // S'abonner aux notifications push
      const subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      console.log('[PUSH] Subscription created:', subscription);

      // Envoyer l'abonnement au serveur
      const subscriptionJson = subscription.toJSON();
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        })
      });

      if (!response.ok) {
        throw new Error('Erreur serveur lors de l\'abonnement');
      }

      setIsSubscribed(true);
      console.log('[PUSH] Successfully subscribed');
      return true;

    } catch (err) {
      console.error('[PUSH] Subscription failed:', err);
      setError(err.message || 'Erreur lors de l\'abonnement');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, swRegistration, token]);

  // Se désabonner
  const unsubscribe = useCallback(async () => {
    if (!swRegistration || !token) {
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const subscription = await swRegistration.pushManager.getSubscription();
      
      if (subscription) {
        // Désabonner côté navigateur
        await subscription.unsubscribe();

        // Désabonner côté serveur
        const subscriptionJson = subscription.toJSON();
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys
          })
        });
      }

      setIsSubscribed(false);
      console.log('[PUSH] Successfully unsubscribed');
      return true;

    } catch (err) {
      console.error('[PUSH] Unsubscribe failed:', err);
      setError(err.message || 'Erreur lors du désabonnement');
      return false;
    } finally {
      setLoading(false);
    }
  }, [swRegistration, token]);

  // Toggle subscription
  const toggleSubscription = useCallback(async () => {
    if (isSubscribed) {
      return await unsubscribe();
    } else {
      return await subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    toggleSubscription
  };
};

export default usePushNotifications;
